import { Search, Plus, Library, User2, Share2, Clock3, Lock, Users, Building2 } from 'lucide-react';
import type { ComponentType } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn } from '@/shared/utils/utils';
import { NOTE_VIEWS, accessMeta, notePreview, sharingSummary, type NoteView } from '../constants';
import type { Note } from '../types';

const VIEW_ICON: Record<NoteView, ComponentType<{ className?: string }>> = {
  all: Library,
  mine: User2,
  shared: Share2,
  recent: Clock3,
  private: Lock,
  users: Users,
  department: Building2,
};

interface NoteSidebarProps {
  notes: Note[];
  counts: Record<NoteView, number>;
  view: NoteView;
  search: string;
  selectedId: string | null;
  loading: boolean;
  creating: boolean;
  isOwner: (n: Note) => boolean;
  onViewChange: (v: NoteView) => void;
  onSearchChange: (v: string) => void;
  onSelect: (n: Note) => void;
  onNew: () => void;
}

function fmtRelative(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '';
  const diff = Date.now() - d;
  const day = 86_400_000;
  if (diff < day) return 'Today';
  if (diff < 2 * day) return 'Yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

export function NoteSidebar({
  notes,
  counts,
  view,
  search,
  selectedId,
  loading,
  creating,
  isOwner,
  onViewChange,
  onSearchChange,
  onSelect,
  onNew,
}: NoteSidebarProps) {
  const library = NOTE_VIEWS.filter((v) => v.group === 'library');
  const access = NOTE_VIEWS.filter((v) => v.group === 'access');

  return (
    <aside className="flex max-h-[45vh] w-full shrink-0 flex-col border-b border-outline bg-surface-container-low md:h-full md:max-h-none md:w-72 md:border-b-0 md:border-r">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-outline p-4">
        <Button onClick={onNew} loading={creating} className="w-full justify-center">
          <Plus className="h-4 w-4" /> New Note
        </Button>
        <Input
          name="notes-search"
          placeholder="Search notes…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          leftIcon={<Search className="h-4 w-4" />}
        />
      </div>

      {/* Views + list */}
      <div className="flex-1 overflow-y-auto">
        <nav className="px-2 py-3">
          <ViewGroup label="Library" views={library} active={view} counts={counts} onSelect={onViewChange} />
          <ViewGroup label="Access" views={access} active={view} counts={counts} onSelect={onViewChange} />
        </nav>

        <div className="border-t border-outline px-2 py-2">
          {loading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : notes.length === 0 ? (
            <p className="px-3 py-6 text-center text-body-sm text-on-surface-variant">
              {search ? 'No matching notes.' : 'No notes in this view.'}
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {notes.map((n) => {
                const selected = n.id === selectedId;
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => onSelect(n)}
                      className={cn(
                        'w-full rounded-card-sm px-3 py-2.5 text-left transition-colors',
                        selected ? 'bg-primary-container/60 ring-1 ring-primary/30' : 'hover:bg-surface-variant',
                      )}
                      aria-current={selected}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-data-md font-semibold text-on-surface">
                          {n.title || 'Untitled Note'}
                        </span>
                        <span className="shrink-0 text-[10px] text-on-surface-variant">{fmtRelative(n.updated_at)}</span>
                      </div>
                      <p className="mt-0.5 truncate text-body-sm text-on-surface-variant">{notePreview(n.notebookText, 60)}</p>
                      <div className="mt-1.5 flex items-center gap-1.5">
                        <Badge tone={accessMeta(n.access_control).tone}>{sharingSummary(n)}</Badge>
                        {isOwner(n) && <span className="text-[10px] text-on-surface-variant">· Owner</span>}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </aside>
  );
}

function ViewGroup({
  label,
  views,
  active,
  counts,
  onSelect,
}: {
  label: string;
  views: { value: NoteView; label: string }[];
  active: NoteView;
  counts: Record<NoteView, number>;
  onSelect: (v: NoteView) => void;
}) {
  return (
    <div className="mb-2">
      <p className="px-3 py-1 text-label-caps uppercase text-on-surface-variant">{label}</p>
      {views.map((v) => {
        const Icon = VIEW_ICON[v.value];
        const isActive = active === v.value;
        return (
          <button
            key={v.value}
            onClick={() => onSelect(v.value)}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-card-sm px-3 py-2 text-data-md transition-colors',
              isActive ? 'bg-primary-container/60 font-semibold text-on-surface' : 'text-on-surface-variant hover:bg-surface-variant',
            )}
            aria-current={isActive}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-left">{v.label}</span>
            <span className="text-[11px] tabular-nums text-on-surface-variant">{counts[v.value] ?? 0}</span>
          </button>
        );
      })}
    </div>
  );
}
